/**
 * MatchReviewPanel — reviewer workspace for one flagged customer.
 *
 * Lists every OFAC candidate above the threshold with its programs and
 * registered addresses, so a reviewer can judge whether the hit is the same
 * party, then record the decision on the audit trail.
 */
import React, { useCallback, useEffect, useState } from 'react';
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';
import ShieldAlert from 'lucide-react/dist/esm/icons/shield-alert';
import { useSanctionsStore } from '../../stores/sanctionsStore';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import { useToast } from '../../../../components/feedback';
import {
  Badge, Button, Card, SlidePanel, Text, TextArea,
} from '../../../../components/ui';
import type { ScreeningMatch, ScreeningResult, ScreeningReviewStatus } from '../../types/aml';

const OFAC_DETAILS_URL = 'https://sanctionssearch.ofac.treas.gov/Details.aspx?id=';

const REVIEW_MAP = {
  pending: { variant: 'neutral', labelKey: 'amlScreeningReviewPending' },
  false_positive: { variant: 'info', labelKey: 'amlScreeningReviewFalsePositive' },
  confirmed_match: { variant: 'danger', labelKey: 'amlScreeningReviewConfirmedMatch' },
} as const;

function formatScore(value: string | null): string {
  if (value == null) return '—';
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? value : parsed.toFixed(1);
}

function formatAddresses(addresses: ScreeningMatch['addresses']): string[] {
  if (!addresses?.length) return [];
  return addresses
    .map((addr) => [addr.address, addr.city_state_zip, addr.country]
      .filter(Boolean).join(', '))
    .filter(Boolean);
}

interface MatchCardProps {
  match: ScreeningMatch;
  t: (key: string) => string;
}

const MatchCard = React.memo<MatchCardProps>(({ match, t }) => {
  const addresses = formatAddresses(match.addresses);

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Text variant="subheading" className="break-words">
            {match.matched_name}
          </Text>
          {match.name_type === 'aka' && match.primary_name ? (
            <Text variant="caption" className="mt-0.5 block">
              {t('amlScreeningAkaOf')}: {match.primary_name}
            </Text>
          ) : null}
        </div>
        <Badge variant="danger" size="md">
          {formatScore(match.score)}%
        </Badge>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <Text variant="label">{t('amlScreeningColList')}</Text>
          <Text variant="body" className="mt-0.5 block">
            {match.list_type ? t(`amlSanctionsListShort_${match.list_type}`) : '—'}
          </Text>
        </div>
        <div>
          <Text variant="label">{t('amlScreeningMatchEntityType')}</Text>
          <Text variant="body" className="mt-0.5 block capitalize">
            {match.entity_type || '—'}
          </Text>
        </div>
      </div>

      <div className="mt-3">
        <Text variant="label">{t('amlScreeningMatchPrograms')}</Text>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {match.programs?.length ? (
            match.programs.map((program) => (
              <Badge key={program} variant="warning" size="sm">{program}</Badge>
            ))
          ) : (
            <Text variant="body">—</Text>
          )}
        </div>
      </div>

      {addresses.length > 0 ? (
        <div className="mt-3">
          <Text variant="label">{t('amlScreeningMatchAddresses')}</Text>
          {addresses.map((address) => (
            <Text key={address} variant="body" className="mt-0.5 block">
              {address}
            </Text>
          ))}
        </div>
      ) : null}

      {match.ofac_uid != null ? (
        <a
          href={`${OFAC_DETAILS_URL}${match.ofac_uid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          {t('amlScreeningViewOnOfac')}
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      ) : null}
    </Card>
  );
});
MatchCard.displayName = 'MatchCard';

interface MatchReviewPanelProps {
  result: ScreeningResult | null;
  onClose: () => void;
}

const MatchReviewPanel: React.FC<MatchReviewPanelProps> = ({ result, onClose }) => {
  const { reviewResult } = useSanctionsStore();
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);
  const { success, error: toastError } = useToast();

  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState<ScreeningReviewStatus | null>(null);

  // Reset the draft note whenever a different customer is opened.
  useEffect(() => {
    setNotes(result?.review_notes ?? '');
  }, [result?.id, result?.review_notes]);

  const handleNotesChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(event.target.value),
    [],
  );

  const handleReview = useCallback(async (status: ScreeningReviewStatus) => {
    if (!result) return;
    setSaving(status);
    try {
      await reviewResult(result.id, status, notes.trim() || null);
      success(t('amlScreeningToastReviewSaved'));
      onClose();
    } catch {
      toastError(t('amlScreeningToastReviewFailed'));
    } finally {
      setSaving(null);
    }
  }, [result, notes, reviewResult, success, toastError, t, onClose]);

  const handleFalsePositive = useCallback(
    () => handleReview('false_positive'), [handleReview],
  );
  const handleConfirm = useCallback(
    () => handleReview('confirmed_match'), [handleReview],
  );

  const reviewMeta = result ? REVIEW_MAP[result.review_status] : null;

  return (
    <SlidePanel
      isOpen={!!result}
      onClose={onClose}
      title={t('amlScreeningReviewPanelTitle')}
      width="max-w-[720px]"
    >
      {result ? (
        <div className="p-6 space-y-5">
          <div>
            <Text variant="label">{t('amlScreeningColCustomer')}</Text>
            <Text variant="heading" className="mt-1 block break-words">
              {result.customer_name_snapshot}
            </Text>
            {result.customer_address_snapshot ? (
              <Text variant="caption" className="mt-1 block">
                {result.customer_address_snapshot}
              </Text>
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <Badge
                variant={result.review_status === 'false_positive' ? 'success' : 'danger'}
                size="sm"
                dot
              >
                {t(
                  result.review_status === 'false_positive'
                    ? 'amlScreeningResultCleared'
                    : 'amlScreeningResultPotentialMatch',
                )}
              </Badge>
              {reviewMeta ? (
                <Badge variant={reviewMeta.variant} size="sm">
                  {t(reviewMeta.labelKey)}
                </Badge>
              ) : null}
              <Text variant="caption">
                {t('amlScreeningBestScore')}: {formatScore(result.best_score)}%
              </Text>
            </div>
          </div>

          <div>
            <Text variant="subheading" className="mb-2 block">
              {t('amlScreeningCandidatesTitle')} ({result.matches.length})
            </Text>
            <div className="space-y-3">
              {result.matches.map((match) => (
                <MatchCard key={match.id} match={match} t={t} />
              ))}
            </div>
          </div>

          <div>
            <TextArea
              label={t('amlScreeningReviewNotesLabel')}
              value={notes}
              onChange={handleNotesChange}
              placeholder={t('amlScreeningReviewNotesPlaceholder')}
              rows={3}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-subtle">
            <Button
              variant="secondary"
              size="lg"
              leftIcon={<CheckCircle2 size={16} />}
              loading={saving === 'false_positive'}
              onClick={handleFalsePositive}
            >
              {t('amlScreeningBtnMarkFalsePositive')}
            </Button>
            <Button
              variant="danger"
              size="lg"
              leftIcon={<ShieldAlert size={16} />}
              loading={saving === 'confirmed_match'}
              onClick={handleConfirm}
            >
              {t('amlScreeningBtnConfirmMatch')}
            </Button>
          </div>
        </div>
      ) : null}
    </SlidePanel>
  );
};

export default MatchReviewPanel;
